/**
 * RelaySimulatorCasePage
 * Dev-only case detail view for the relay simulator.
 * Route: /relay/simulator/:caseId
 *
 * Left panel: mailbox list for the case
 * Right panel: paginated message list for selected mailbox
 * Controls: reply (US-25/26), status events (US-27), clear mailbox sink
 *
 * Production safety: redirects to /settings/agent-email when simulator is disabled.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Inbox, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { StructuredDataView, colors } from '@resida/ui';
import { PageHeader } from '../components/layout/index';
import { EmptyState } from '../components/layout/index';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  fetchSimulatorEnabled,
  fetchCaseMailboxes,
  fetchMailboxMessages,
  postSimulatedInbound,
  postSimulatedStatus,
  deleteMailboxSink,
  type MailboxSummary,
  type SimulatorMessageRow,
  type PaginatedMessages,
  type SimulateStatusBody,
} from '../lib/relay-simulator.api';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function decodeBase64(b64: string): string {
  try {
    return atob(b64);
  } catch {
    return b64;
  }
}

// Content type detection for structured reply controls
type ContentKind = 'prompt_choice' | 'prompt_confirm' | 'prompt_data' | 'text' | null;

function detectContentKind(content: unknown): ContentKind {
  if (!content || typeof content !== 'object') return null;
  const c = content as Record<string, unknown>;
  if (c.kind === 'prompt_choice') return 'prompt_choice';
  if (c.kind === 'prompt_confirm') return 'prompt_confirm';
  if (c.kind === 'prompt_data') return 'prompt_data';
  if (c.kind === 'text') return 'text';
  return null;
}

function getContentOptions(content: unknown): string[] {
  if (!content || typeof content !== 'object') return [];
  const c = content as Record<string, unknown>;
  if (!Array.isArray(c.options)) return [];
  return c.options.filter((o): o is string => typeof o === 'string');
}

// Returns the plain-text preview for a known content shape, or null when the shape isn't
// recognized — the caller falls back to StructuredDataView instead of a raw JSON dump.
function getContentText(content: unknown): string | null {
  if (typeof content === 'string') return content;
  if (!content || typeof content !== 'object') return '';
  const c = content as Record<string, unknown>;
  if (typeof c.body === 'string') return c.body;   // {kind, body} relay format
  if (typeof c.text === 'string') return c.text;   // {type, text} structured_v1 format
  return null;
}

// ---------------------------------------------------------------------------
// Status control for individual messages
// ---------------------------------------------------------------------------

const STATUS_OPTIONS: SimulateStatusBody['status'][] = ['delivered', 'bounced', 'failed', 'read'];

interface MessageStatusControlProps {
  messageId: string;
  onStatusApplied: (messageId: string, newStatus: string) => void;
}

function MessageStatusControl({ messageId, onStatusApplied }: MessageStatusControlProps) {
  const [selectedStatus, setSelectedStatus] = useState<SimulateStatusBody['status']>('delivered');
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleApply = useCallback(async () => {
    setApplying(true);
    setError(null);
    try {
      await postSimulatedStatus(messageId, { status: selectedStatus });
      onStatusApplied(messageId, selectedStatus);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply status.');
    } finally {
      setApplying(false);
    }
  }, [messageId, selectedStatus, onStatusApplied]);

  return (
    <div className="flex items-center gap-2 mt-2 flex-wrap">
      <select
        value={selectedStatus}
        onChange={(e) => setSelectedStatus(e.target.value as SimulateStatusBody['status'])}
        className="rounded-md border border-input bg-background px-2 py-1 text-xs text-foreground outline-none"
        aria-label="Select status to apply"
      >
        {STATUS_OPTIONS.map((s) => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>
      <Button
        size="sm"
        variant="outline"
        data-testid={`simulator-status-btn-${messageId}`}
        disabled={applying}
        onClick={handleApply}
        className="text-xs"
      >
        {applying ? 'Applying…' : 'Apply status'}
      </Button>
      {error && (
        <span className="text-xs text-destructive" role="alert">{error}</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Raw MIME expander
// ---------------------------------------------------------------------------

function RawMimeExpander({ messageId, rawMimeBase64 }: { messageId: string; rawMimeBase64: string }) {
  const [open, setOpen] = useState(false);
  const decoded = decodeBase64(rawMimeBase64);

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
        aria-expanded={open}
        aria-controls={`raw-mime-${messageId}`}
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        View raw MIME
      </button>
      {open && (
        <pre
          id={`raw-mime-${messageId}`}
          data-testid={`simulator-raw-mime-${messageId}`}
          className="mt-1 p-2 rounded bg-muted text-xs font-mono whitespace-pre-wrap break-all overflow-auto max-h-48"
          role="region"
          aria-label="Raw MIME content"
        >
          {decoded}
        </pre>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Message row
// ---------------------------------------------------------------------------

interface MessageRowProps {
  msg: SimulatorMessageRow;
  onStatusApplied: (messageId: string, newStatus: string) => void;
}

function MessageRow({ msg, onStatusApplied }: MessageRowProps) {
  const contentText = getContentText(msg.content);

  return (
    <div
      data-testid={`simulator-message-row-${msg.id}`}
      className="p-3 rounded-md border border-border bg-card space-y-1"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {msg.direction === 'inbound' ? (
            <ArrowDownLeft className="h-3 w-3 shrink-0 text-info" aria-label="Inbound" />
          ) : (
            <ArrowUpRight className="h-3 w-3 shrink-0 text-success" aria-label="Outbound" />
          )}
          <span className="text-xs font-mono text-muted-foreground truncate">
            {msg.fromAddress} → {msg.toAddress}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs px-1.5 py-0.5 rounded bg-secondary text-secondary-foreground">
            {msg.status}
          </span>
          <span className="text-xs text-muted-foreground">{formatDate(msg.createdAt)}</span>
        </div>
      </div>

      {msg.subject && (
        <p className="text-sm font-medium text-foreground truncate">{msg.subject}</p>
      )}

      {contentText !== null ? (
        <p className="text-xs text-muted-foreground line-clamp-2 break-words">{contentText}</p>
      ) : (
        <div
          className="p-2 rounded text-xs overflow-x-auto"
          style={{ backgroundColor: colors.gray[900], border: `1px solid ${colors.gray[700]}` }}
        >
          <StructuredDataView data={msg.content} />
        </div>
      )}

      {msg.direction === 'outbound' && msg.rawMimeBase64 && (
        <RawMimeExpander messageId={msg.id} rawMimeBase64={msg.rawMimeBase64} />
      )}

      <MessageStatusControl messageId={msg.id} onStatusApplied={onStatusApplied} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reply controls (US-25 free text + US-26 structured)
// ---------------------------------------------------------------------------

interface ReplyControlsProps {
  caseId: string;
  selectedAddress: string;
  recentMessage: SimulatorMessageRow | null;
  onReplySent: () => void;
}

function ReplyControls({ caseId, selectedAddress, recentMessage, onReplySent }: ReplyControlsProps) {
  const contentKind = recentMessage ? detectContentKind(recentMessage.content) : null;
  const [freeText, setFreeText] = useState('');
  const [selectedOption, setSelectedOption] = useState('');
  const [dataValue, setDataValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFreeTextSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!freeText.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await postSimulatedInbound({
        caseId,
        fromAddress: selectedAddress,
        // toAddress omitted — server derives from respondsToMessageId
        content: { kind: 'text', body: freeText.trim() },
        respondsToMessageId: recentMessage?.id,
      });
      setFreeText('');
      onReplySent();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reply.');
    } finally {
      setSubmitting(false);
    }
  }, [caseId, selectedAddress, freeText, recentMessage, onReplySent]);

  const handleStructuredSubmit = useCallback(async (content: unknown) => {
    setSubmitting(true);
    setError(null);
    try {
      await postSimulatedInbound({
        caseId,
        fromAddress: selectedAddress,
        // toAddress omitted — server derives from respondsToMessageId
        content,
        respondsToMessageId: recentMessage?.id,
      });
      onReplySent();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send structured reply.');
    } finally {
      setSubmitting(false);
    }
  }, [caseId, selectedAddress, recentMessage, onReplySent]);

  const shouldShowStructured = contentKind && contentKind !== 'text' && contentKind !== null;

  return (
    <div className="mt-4 space-y-4 border-t border-border pt-4">
      {error && (
        <div
          data-testid="simulator-reply-error"
          className="p-2 bg-destructive/10 border border-destructive/30 rounded text-destructive text-xs"
          role="alert"
        >
          {error}
        </div>
      )}

      {/* US-25: Free text reply */}
      <form
        data-testid="simulator-reply-text"
        onSubmit={handleFreeTextSubmit}
        aria-label="Free text reply"
        className="space-y-2"
      >
        <label htmlFor="reply-free-text" className="text-xs font-medium text-muted-foreground block">
          Free text reply
        </label>
        <textarea
          id="reply-free-text"
          data-testid="simulator-reply-text-input"
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          rows={3}
          placeholder="Type a reply…"
          className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring resize-none"
        />
        <Button
          type="submit"
          size="sm"
          data-testid="simulator-reply-text-submit"
          disabled={submitting || !freeText.trim()}
        >
          {submitting ? 'Sending…' : 'Send reply'}
        </Button>
      </form>

      {/* US-26: Structured response controls */}
      {shouldShowStructured && (
        <div data-testid="simulator-reply-structured" aria-label="Structured response controls">
          <p className="text-xs font-medium text-muted-foreground mb-2">
            Structured response
            <span className="ml-1 text-muted-foreground/60">({contentKind})</span>
          </p>

          {contentKind === 'prompt_choice' && (
            <div className="flex flex-wrap gap-2">
              {getContentOptions(recentMessage?.content).map((opt) => (
                <Button
                  key={opt}
                  type="button"
                  size="sm"
                  variant={selectedOption === opt ? 'default' : 'outline'}
                  disabled={submitting}
                  data-testid={`simulator-choice-btn-${opt}`}
                  onClick={() => {
                    setSelectedOption(opt);
                    void handleStructuredSubmit({ kind: 'prompt_choice_response', selectedOption: opt });
                  }}
                  className="text-xs"
                >
                  {opt}
                </Button>
              ))}
            </div>
          )}

          {contentKind === 'prompt_confirm' && (
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                disabled={submitting}
                data-testid="simulator-confirm-yes"
                onClick={() => void handleStructuredSubmit({ kind: 'prompt_confirm_response', confirmed: true })}
                className="text-xs"
              >
                Yes
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={submitting}
                data-testid="simulator-confirm-no"
                onClick={() => void handleStructuredSubmit({ kind: 'prompt_confirm_response', confirmed: false })}
                className="text-xs"
              >
                No
              </Button>
            </div>
          )}

          {contentKind === 'prompt_data' && (
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label htmlFor="reply-data-value" className="sr-only">Data response value</label>
                <input
                  id="reply-data-value"
                  data-testid="simulator-reply-data-input"
                  type="text"
                  value={dataValue}
                  onChange={(e) => setDataValue(e.target.value)}
                  placeholder="Enter response value…"
                  className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              <Button
                type="button"
                size="sm"
                disabled={submitting || !dataValue.trim()}
                data-testid="simulator-data-submit"
                onClick={() => void handleStructuredSubmit({ kind: 'prompt_data_response', value: dataValue.trim() })}
                className="text-xs"
              >
                Submit
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mailbox panel
// ---------------------------------------------------------------------------

interface MailboxPanelProps {
  caseId: string;
  mailboxes: MailboxSummary[];
  selectedAddress: string | null;
  onSelect: (address: string) => void;
  onClearSink: (address: string) => void;
}

function MailboxPanel({ mailboxes, selectedAddress, onSelect, onClearSink }: MailboxPanelProps) {
  const [clearing, setClearing] = useState<string | null>(null);
  const [clearError, setClearError] = useState<string | null>(null);

  const handleClearSink = useCallback(async (address: string) => {
    setClearing(address);
    setClearError(null);
    try {
      await deleteMailboxSink(address);
      onClearSink(address);
    } catch (err) {
      setClearError(err instanceof Error ? err.message : 'Failed to clear mailbox sink.');
    } finally {
      setClearing(null);
    }
  }, [onClearSink]);

  if (mailboxes.length === 0) {
    return (
      <div data-testid="simulator-mailbox-list">
        <EmptyState
          icon={Inbox}
          title="No mailboxes"
          description="No mailbox activity recorded for this case yet."
        />
      </div>
    );
  }

  return (
    <div data-testid="simulator-mailbox-list" className="space-y-1">
      {clearError && (
        <div className="text-xs text-destructive mb-2" role="alert">{clearError}</div>
      )}
      {mailboxes.map((mb) => (
        <div key={mb.address} className="space-y-0.5">
          <button
            type="button"
            onClick={() => onSelect(mb.address)}
            className={`w-full text-left rounded-md px-3 py-2 text-xs transition-colors ${
              selectedAddress === mb.address
                ? 'bg-primary/10 text-primary font-medium'
                : 'hover:bg-accent/50 text-foreground'
            }`}
            aria-pressed={selectedAddress === mb.address}
            aria-label={`Select mailbox ${mb.address}`}
          >
            <p className="font-mono truncate">{mb.address}</p>
            <p className="text-muted-foreground mt-0.5">
              {mb.inboundCount} in · {mb.outboundCount} out · {mb.mockSinkCount} sink
            </p>
          </button>
          <div className="px-3">
            <button
              type="button"
              data-testid={`simulator-clear-sink-${mb.address}`}
              onClick={() => void handleClearSink(mb.address)}
              disabled={clearing === mb.address}
              className="text-[10px] text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
            >
              {clearing === mb.address ? 'Clearing…' : 'Clear sink'}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Message panel
// ---------------------------------------------------------------------------

interface MessagePanelProps {
  caseId: string;
  selectedAddress: string | null;
}

function MessagePanel({ caseId, selectedAddress }: MessagePanelProps) {
  const [paginatedMessages, setPaginatedMessages] = useState<PaginatedMessages | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadMessages = useCallback(async (address: string, cursor?: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchMailboxMessages(address, cursor);
      setPaginatedMessages((prev) => {
        if (cursor && prev) {
          return {
            ...data,
            messages: [...prev.messages, ...data.messages],
          };
        }
        return data;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load messages.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedAddress) {
      setPaginatedMessages(null);
      return;
    }
    loadMessages(selectedAddress);
  }, [selectedAddress, loadMessages]);

  const handleStatusApplied = useCallback((messageId: string, newStatus: string) => {
    setPaginatedMessages((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        messages: prev.messages.map((m) =>
          m.id === messageId ? { ...m, status: newStatus } : m
        ),
      };
    });
  }, []);

  const handleReplySent = useCallback(() => {
    if (selectedAddress) {
      loadMessages(selectedAddress);
    }
  }, [selectedAddress, loadMessages]);

  if (!selectedAddress) {
    return (
      <div data-testid="simulator-message-list" className="h-full flex items-center justify-center">
        <p className="text-sm text-muted-foreground">Select a mailbox to view messages.</p>
      </div>
    );
  }

  const messages = paginatedMessages?.messages ?? [];
  const mostRecentOutbound = messages.find((m) => m.direction === 'outbound') ?? null;

  return (
    <div data-testid="simulator-message-list" className="space-y-3">
      {loading && (
        <div data-testid="simulator-messages-loading" className="space-y-2">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      )}

      {error && (
        <div
          data-testid="simulator-messages-error"
          className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm flex items-center justify-between gap-3"
          role="alert"
        >
          <span>{error}</span>
          <Button variant="outline" size="sm" onClick={() => loadMessages(selectedAddress)}>
            Retry
          </Button>
        </div>
      )}

      {!loading && !error && messages.length === 0 && (
        <EmptyState
          icon={Inbox}
          title="No messages"
          description="No messages in this mailbox yet."
          data-testid="simulator-messages-empty"
        />
      )}

      {!loading && messages.length > 0 && (
        <div className="space-y-2">
          {messages.map((msg) => (
            <MessageRow key={msg.id} msg={msg} onStatusApplied={handleStatusApplied} />
          ))}
        </div>
      )}

      {paginatedMessages?.nextCursor && !loading && (
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => loadMessages(selectedAddress, paginatedMessages.nextCursor!)}
        >
          Load more
        </Button>
      )}

      {/* Reply controls */}
      <ReplyControls
        caseId={caseId}
        selectedAddress={selectedAddress}
        recentMessage={mostRecentOutbound}
        onReplySent={handleReplySent}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// RelaySimulatorCasePage
// ---------------------------------------------------------------------------

export function RelaySimulatorCasePage() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const [checkingEnabled, setCheckingEnabled] = useState(true);
  const [mailboxes, setMailboxes] = useState<MailboxSummary[]>([]);
  const [loadingMailboxes, setLoadingMailboxes] = useState(true);
  const [mailboxesError, setMailboxesError] = useState<string | null>(null);
  const [selectedAddress, setSelectedAddress] = useState<string | null>(null);

  // Production-safety: check if simulator is enabled on mount
  useEffect(() => {
    let cancelled = false;
    fetchSimulatorEnabled().then((enabled) => {
      if (cancelled) return;
      setCheckingEnabled(false);
      if (!enabled) {
        navigate('/settings/agent-email', { replace: true });
      }
    });
    return () => { cancelled = true; };
  }, [navigate]);

  const loadMailboxes = useCallback(async () => {
    if (!caseId) return;
    setLoadingMailboxes(true);
    setMailboxesError(null);
    try {
      const data = await fetchCaseMailboxes(caseId);
      setMailboxes(data);
      if (data.length > 0 && !selectedAddress) {
        setSelectedAddress(data[0]!.address);
      }
    } catch (err) {
      setMailboxesError(err instanceof Error ? err.message : 'Failed to load mailboxes.');
    } finally {
      setLoadingMailboxes(false);
    }
  }, [caseId, selectedAddress]);

  useEffect(() => {
    if (!checkingEnabled) {
      loadMailboxes();
    }
  }, [checkingEnabled, loadMailboxes]);

  const handleClearSink = useCallback(() => {
    // Refresh mailboxes after clearing sink
    loadMailboxes();
  }, [loadMailboxes]);

  if (!caseId) {
    return (
      <div data-testid="relay-simulator-case-page" className="max-w-6xl mx-auto pb-6 px-[var(--page-padding-x)]">
        <p className="text-sm text-destructive">Missing case ID.</p>
      </div>
    );
  }

  if (checkingEnabled) {
    return (
      <div data-testid="relay-simulator-case-page" className="max-w-6xl mx-auto pb-6 px-[var(--page-padding-x)]">
        <div className="space-y-3 mt-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="relay-simulator-case-page"
      className="max-w-6xl mx-auto pb-6 px-[var(--page-padding-x)]"
    >
      <PageHeader
        title={`Simulator: case ${caseId}`}
        backLink={{ label: 'Back to simulator', to: '/relay/simulator' }}
      />

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
        {/* Left panel: mailboxes */}
        <aside className="border border-border rounded-lg p-4 bg-card">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Mailboxes
          </h2>

          {mailboxesError && (
            <div
              data-testid="simulator-mailboxes-error"
              className="p-2 bg-destructive/10 border border-destructive/30 rounded text-destructive text-xs mb-3"
              role="alert"
            >
              {mailboxesError}
            </div>
          )}

          {loadingMailboxes && (
            <div className="space-y-2">
              {[...Array(2)].map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          )}

          {!loadingMailboxes && !mailboxesError && (
            <MailboxPanel
              caseId={caseId}
              mailboxes={mailboxes}
              selectedAddress={selectedAddress}
              onSelect={setSelectedAddress}
              onClearSink={handleClearSink}
            />
          )}
        </aside>

        {/* Right panel: messages */}
        <section aria-labelledby="messages-panel-heading" className="border border-border rounded-lg p-4 bg-card">
          <h2 id="messages-panel-heading" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            {selectedAddress ? (
              <span className="font-mono normal-case">{selectedAddress}</span>
            ) : (
              'Messages'
            )}
          </h2>
          <MessagePanel caseId={caseId} selectedAddress={selectedAddress} />
        </section>
      </div>
    </div>
  );
}
